import httpClient from 'hub-http/clients/apiClient';
import { createRpcClientV2 } from 'rpc-client-utils';
import { submitInteraction } from 'self-service-api/api/submitInteraction';
import { BUNDLE } from 'self-service-api/constants/UpgradeProducts';
import { sourceKeys, sources } from 'ui-addon-upgrades/_core/common/data/upgradeData/properties/sources';
import { getUpgradeConfigWithPurchaseMotionsRpc } from 'upgrade-management-service-types';
// @ts-expect-error dependency missing types
import UpgradeProductToApiNameMap from 'self-service-api/constants/UpgradeProductToApiNameMap';
import { trackFailedApiNameMap } from '../_core/utils/metricUtils';
import { UPGRADE_BANNER_TYPES } from './types';
import { logRavenException } from '../_core/utils/errors';
const rpcClient = createRpcClientV2({
  hubHttpClient: httpClient
});
export let UpgradePointStatus;
(function (UpgradePointStatus) {
  UpgradePointStatus["STALE"] = "STALE";
  UpgradePointStatus["ACTIVE"] = "ACTIVE";
  UpgradePointStatus["ARCHIVED"] = "ARCHIVED";
  UpgradePointStatus["PLANNED"] = "PLANNED";
})(UpgradePointStatus || (UpgradePointStatus = {}));
const upgradePointPromises = {};

// viewedUpgradePointsCache is a module cache that stores whether a user has
// viewed an upgrade point, via a unique upgradeKey. This allows to us to
// prevent usage trackers from firing more than once per app session.
export const viewedUpgradePointsCache = {};
export const updateViewedUpgradePointsCache = (upgradeKey, onCacheUpdate) => {
  if (!viewedUpgradePointsCache[upgradeKey]) {
    viewedUpgradePointsCache[upgradeKey] = true;
    if (onCacheUpdate) onCacheUpdate();
  }
};
export function getApp() {
  return window.location.pathname.split('/')[1];
}
export function getScreen() {
  return window.location.pathname;
}
export function getHubAndTier(upgradeProduct) {
  return {
    hub: upgradeProduct.split('-')[0],
    tier: upgradeProduct.split('-')[1]
  };
}
export function getHubAndTierFromApiName(apiName) {
  const hub = apiName.split('-')[0];
  const tier = apiName.split('-')[2];
  return {
    hub: hub === 'crm' ? 'suite' : hub,
    tier: !tier && hub === 'hubspot' ? 'free' : tier // free tools api name has no tier
  };
}
export function isCommerceHub(hub) {
  return hub === 'commerce';
}
export function _getHubAndTierProperties({
  upgradeProduct,
  apiName
}) {
  if (apiName) return getHubAndTierFromApiName(apiName);
  if (!upgradeProduct || upgradeProduct === BUNDLE) {
    return {
      hub: null,
      tier: null
    };
  } else {
    return getHubAndTier(upgradeProduct);
  }
}
export function fetchUpgradePoint({
  upgradeKey,
  includePurchaseMotions
}) {
  if (!upgradeKey || upgradeKey === '') {
    return Promise.resolve();
  }
  return rpcClient.call(getUpgradeConfigWithPurchaseMotionsRpc, {
    pathParameters: {
      upgradeKey
    },
    queryParameters: {
      includePurchaseMotions
    }
  }).catch(error => {
    var _error$cause;
    const message = ((_error$cause = error.cause) === null || _error$cause === void 0 ? void 0 : _error$cause.status) === 404 ? `${upgradeKey}: upgrade point may not exist in the UMS.` : `${upgradeKey}: unexpected error fetching upgrade config`;
    logRavenException({
      customMessage: message,
      error,
      uniqueId: upgradeKey,
      fingerprint: ['fetchUpgradePoint']
    });
    throw error;
  });
}
export function getUpgradePointPromise({
  upgradeKey,
  includePurchaseMotions
}) {
  if (upgradePointPromises[upgradeKey]) {
    return upgradePointPromises[upgradeKey];
  }
  const upgradePointPromise = new Promise(resolve => {
    resolve(fetchUpgradePoint({
      upgradeKey,
      includePurchaseMotions
    }));
  });
  upgradePointPromises[upgradeKey] = upgradePointPromise;
  return upgradePointPromise;
}
export const getRepName = (repInfo = {}) => {
  if (repInfo && repInfo.name) {
    return repInfo.name;
  }
  return 'Not assigned';
};
export function getUpgradeConfigTrackingProperties(upgradeConfig) {
  return {
    app: getApp(),
    screen: getScreen(),
    feature: upgradeConfig.featureStoreFeature,
    upgradeKey: upgradeConfig.upgradeKey,
    pointType: upgradeConfig.pointType
    //we intentionally don't track upgradeProduct and apiName at the level where we fetch the upgrade config.
  };
}
export function getUpgradeDataTrackingProperties(upgradeData) {
  const apiNameMaybe = upgradeData.apiName || UpgradeProductToApiNameMap[upgradeData.upgradeProduct];
  const {
    hub,
    tier
  } = _getHubAndTierProperties({
    upgradeProduct: upgradeData.upgradeProduct,
    apiName: apiNameMaybe
  });
  return {
    app: upgradeData.app,
    screen: upgradeData.screen,
    uniqueId: upgradeData.uniqueId,
    upgradeProduct: upgradeData.upgradeProduct,
    apiName: apiNameMaybe,
    source: upgradeData.source,
    upgradeKey: upgradeData.upgradeKey,
    pointType: upgradeData.pointType,
    //the feature defined in UpgradeData is equivalent to the featureStoreFeature of the UpgradeConfig
    feature: upgradeData.feature,
    upgradeSource: upgradeData.upgradeSource,
    hub,
    tier
  };
}
export const getSource = (app, screen, uniqueId) => `${app}-${screen}-${sources[sourceKeys.GENERAL]}-${uniqueId}`;
export const warnArchivedUpgradePointUse = upgradeKey => {
  console.warn(`Upgrade point '${upgradeKey}' is archived, and should not be used in-app. If this upgrade point should not be archived, it can be restored through the UMS at https://tools.hubteamqa.com/ums/upgrade-points/${upgradeKey}.`);
};
export const mapPurchaseMotionApiName = (canViewNewSeats = false, upgradeProduct, source, apiName) => {
  if (canViewNewSeats && !apiName) {
    if (UpgradeProductToApiNameMap[upgradeProduct]) {
      return UpgradeProductToApiNameMap[upgradeProduct];
    } else {
      trackFailedApiNameMap(source, 'no api name', upgradeProduct);
    }
  }
  return apiName;
};
export const trackPotentialBannerInteraction = (pointType, upgradeKey) => {
  if (pointType && upgradeKey && Object.keys(UPGRADE_BANNER_TYPES).includes(pointType)) {
    return submitInteraction(upgradeKey);
  }
  return Promise.resolve();
};

/**
 * Unit and integration testing only
 */
function resetUpgradePointCaches() {
  Object.keys(upgradePointPromises).forEach(upgradeKey => {
    delete upgradePointPromises[upgradeKey];
  });
  Object.keys(viewedUpgradePointsCache).forEach(upgradeKey => {
    delete viewedUpgradePointsCache[upgradeKey];
  });
}
export const __TEST_UTILS__ = {
  resetUpgradePointCaches
};